import React, { Suspense, useCallback, useEffect, useState } from 'react';

import { Backdrop, Button, CircularProgress, Divider, IconButton, makeStyles, Paper, Tooltip, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import Select, { components } from 'react-select';
import { useRef } from 'react';
import { useLocalization } from 'contexto/localization/useLocalization';
import { createPortal } from 'react-dom';

const useStyles = makeStyles({
    title: {
        fontSize: 12,
        marginTop: -10,
        marginBottom: 15,
        marginLeft: 10,
        backgroundColor: "white",
        paddingLeft: 4,
        paddingRight: 4,
        display: "table",
        borderRadius: 5,
    },
    paper: {
        padding: 0,
    },
});

const SelectMenuButton = (props) => {
    return (
        <components.MenuList  {...props}>
            {props.children}
            {props?.selectProps?.onNewElement &&
            <div>
                <Divider/>
                <Button 
                    variant="text"
                    color="primary"
                    startIcon={<AddIcon />}
                    sx={{ mt: "2px", mb: "2px" }}
                    onClick={() => props?.selectProps?.onNewElement()}
                    fullWidth
                >
                        {props?.selectProps?.newElementText}
                </Button>
            </div>
                
            }
        </components.MenuList >
    );
};

/**
 * Opción de selección
 * @typedef {Object} SelectOption
 * @property {string} label Etiqueta de elemento
 * @property {string | number | undefined} value Valor de elemento
 * @property {any} payload Valores
 */

/**
 * Especifica la función a ser ejecutada al retornar lista con datos
 * @callback OnLoadOptions
 * @param {SelectOption} value Valor seleccionado o primer valor de la lista
 * @param {SelectOption[]} data Lista de elementos
 * @returns {void}
 */


/**
 * Especifica la función a ser ejecutada al retornar lista vacía
 * @callback OnEmptyOptions
 * @returns {void}
 */

/**
 * Especifica la función a ser ejecutada al cambiar selección
 * @callback OnChange
 * @param {{target: { name: string, label: string, value: SelectOption | undefined }, value: SelectOption | undefined, payload: any}} action Acción
 * @returns {void}
 */

// {target: { name: string, label: string, value: SelectOption | undefined }, value: SelectOption | undefined, payload: any}

/**
 *
 * @typedef {Object} Props
 * @property {string} name Nombre del selector
 * @property {string} label Etiqueta del selector
 * @property {'small' | 'default'} size Tamaño del selector
 * @property {string | number | undefined} width Ancho de selector
 * @property {any} value Valor seleccionado
 * @property {() => promise<SelectOption[]> | SelectOption[]} options Etiqueta de botón
 * @property {boolean} disabled Si está deshabilitada la selección
 * @property {boolean} required Si es un campo requerido
 * @property {boolean} isClearable Si la selección se puede limpiar
 * @property {any} NewElementModal Etiqueta de botón
 * @property {any} newElementModalProps Etiqueta de botón
 * @property {OnLoadOptions} onLoadOptions Evento al retornar lista con datos
 * @property {OnEmptyOptions} onEmptyOptions Evento al retornar lista vacía
 * @property {OnChange} onChange Evento al cambiar selección
 */

/**
 * Selector
 * @param {Props} props
 * @param {ChildrenFn} props.children
 * @returns
 */
const PromiseSelect = ({ name, label, size, width, value, options, disabled, required = false, isClearable, NewElementModal, newElementModalProps = {}, onLoadOptions, onEmptyOptions, onChange, usePortal = true, labelDescription, showOptionDescriptions = false }) => {
    // Idioma
    const { strings } = useLocalization();

    const [options0, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const selectRef = useRef(null);
    const [openNewElementModal, setOpenNewElementModal] = useState(false);
    const [payload, setPayload] = useState([]);

    const styles = useStyles();

    const cargarData = useCallback(() => {
        setIsLoading(true);
        if (typeof options === "function") {
            const loadOptions = async () => {
                const opts = await options();
                setPayload(opts?.filter(o => o.payload !== undefined)?.map(o => ({ key: o.value, value: o.payload })));
                setOptions(opts);
                setIsLoading(false);
            }
            loadOptions();
        } else {
            setPayload(options?.filter(o => o.payload !== undefined)?.map(o => ({ key: o.value, value: o.payload })));
            setOptions(options);
            setIsLoading(false);
        }
    }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        cargarData();
    }, [cargarData]);

    useEffect(() => {
        if (!isLoading && value !== undefined && value !== null) {
            const v = options0.find(o => o.value === value) ?? (options0.length > 0 ? options0[0] : undefined);
            if (onLoadOptions !== undefined && v !== undefined) onLoadOptions(v, options0);
            if (onEmptyOptions !== undefined && v === undefined) onEmptyOptions();
        }
    }, [options0, value, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    const _onChange = (e) => {
        onChange({
            target: { name: name, label: e ? e.label : undefined, value: e ? e.value : undefined },
            value: e,
            payload: payload.find(o => o.key === e.value),
        });
    }

    return (
        <div style={{ width: width ? width : undefined, position: 'relative' }}>
            <Paper variant="outlined" className={styles.paper} style={{ paddingBottom: size === "small" ? 1 : 8 }}>
                <Typography className={styles.title} color="textSecondary" gutterBottom>
                    {label + (required ? " *" : "")}
                    {labelDescription && (
                        <Tooltip title={labelDescription} arrow>
                            <IconButton
                                size="small"
                                aria-label={`Ayuda: ${label}`}
                                style={{ padding: 2, marginLeft: 2, verticalAlign: 'middle' }}
                            >
                                <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Typography>
                <Select
                    ref={selectRef}
                    placeholder={label + (required ? " *" : "")}
                    onChange={_onChange}
                    options={options0}
                    isLoading={isLoading} 
                    isDisabled={disabled ? true : false}
                    noOptionsMessage={() => strings.components.sistema.sinResultados}
                    value={((value !== undefined && value !== null) && !isLoading)
                        ? (options0.find(o => o.value === value) ?? (options0.length > 0 ? options0[0] : null))
                        : null
                    }
                    formatOptionLabel={(option, { context, selectValue }) => {
                        if (!showOptionDescriptions || context !== 'menu' || !option.description) {
                            return option.label;
                        }
                        const isSelected = selectValue?.some(selectedOption => selectedOption.value === option.value);
                        return (
                            <div>
                                <Typography variant="body2">{option.label}</Typography>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    style={{ whiteSpace: 'normal', color: isSelected ? 'inherit' : undefined }}
                                >
                                    {option.description}
                                </Typography>
                            </div>
                        );
                    }}
                    isClearable={isClearable ? true : false}
                    components={{ MenuList: SelectMenuButton }}
                    onNewElement={NewElementModal
                        ? () => setOpenNewElementModal(true)
                        : undefined
                    }
                    newElementText={strings.components.sistema.nuevoElemento}

                    styles={{
                        control: (base) => ({
                            ...base,
                            border: 0,
                            boxShadow: 'none',
                            marginTop: size === "small" ? '-20px' : '-15px',
                            backgroundColor: 'unset'
                        }),
                        menu: (base) => ({
                            ...base,
                            zIndex: 999,
                        }),
                        menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                        })
                    }}
                    menuPortalTarget={usePortal ? document.body : undefined}
                    menuPosition={usePortal ? 'fixed' : 'absolute'}
                />
                {!disabled && required && (
                    <input
                        tabIndex={-1}
                        autoComplete="off"
                        style={{
                            opacity: 0,
                            width: 1,
                            height: 0,
                            position: "absolute"
                        }}
                        value={value}
                        onChange={() => {}}
                        onFocus={() => selectRef?.current?.focus()}
                        required={required}
                    />
                )}
            </Paper>
            {NewElementModal && openNewElementModal &&
                createPortal(
                    <Suspense fallback={<Backdrop open={true} style={{ zIndex: 9999, color: '#fff' }}><CircularProgress color="inherit" /></Backdrop>}>
                        <NewElementModal 
                            {...newElementModalProps} 
                            open={openNewElementModal} 
                            onSave={() => {
                                cargarData();
                                setOpenNewElementModal(false);
                            }} 
                            onClose={() => setOpenNewElementModal(false)} 
                        />
                    </Suspense>
                , document.body)
            }
        </div>
    );
};

export default PromiseSelect;
